import {
  FETCH_WORKFLOW_CONFIGS,
  FETCH_WORKFLOW_CONFIGS_SUCCESS,
  FETCH_WORKFLOW_CONFIGS_FAILED,
  EDIT_WORKFLOW_CONFIG
} from "../store/types";
import { firebase } from '../config/configureFirebase';
import store from '../store/store';
import { onValue, push, set, remove } from "firebase/database";
import { getVehicleSize, normalizeShipmentType } from "../other/bookingWorkflow";

const slotDirections = ["add", "deduct", "none"];

const normalizeBoolean = (value, fallback = false) => value === undefined || value === null ? fallback : value === true;

const toFlatWorkflowConfig = (id, config = {}) => {
  const forms = config.forms || {};
  const workflows = config.workflows || {};
  const slotRule = config.slotRule || {};
  const emailTriggers = config.emailTriggers || {};
  return {
    id,
    vehicleTypeId: config.vehicleTypeId || "",
    vehicleTypeName: config.vehicleTypeName || "",
    vehicleSize: config.vehicleSize || getVehicleSize({ vehicleTypeName: config.vehicleTypeName }) || "",
    shipmentType: config.shipmentType || "",
    active: config.active !== false,
    formsPrimary: normalizeBoolean(forms.primary, true),
    formsDomestic: normalizeBoolean(forms.domestic),
    formsImport: normalizeBoolean(forms.import),
    formsExport: normalizeBoolean(forms.export),
    workflowsEmptyReturn: normalizeBoolean(workflows.emptyReturn),
    workflowsEmptyPickup: normalizeBoolean(workflows.emptyPickup),
    workflowsSlotRules: normalizeBoolean(workflows.slotRules),
    workflowsBookingReceipt: normalizeBoolean(workflows.bookingReceipt, true),
    slotRuleEnabled: normalizeBoolean(slotRule.enabled),
    slotRuleDirection: slotDirections.indexOf(slotRule.direction) !== -1 ? slotRule.direction : "none",
    slotRuleAmount: slotRule.amount !== undefined && !isNaN(Number(slotRule.amount)) ? Number(slotRule.amount) : 0,
    emailBookingCompletedReceipt: normalizeBoolean(emailTriggers.bookingCompletedReceipt, true),
    emailEmptyReturnNotification: normalizeBoolean(emailTriggers.emptyReturnNotification),
    emailEmptyPickupNotification: normalizeBoolean(emailTriggers.emptyPickupNotification)
  };
};

const toFirebaseWorkflowConfig = (config = {}) => {
  const slotRules = config.workflowsSlotRules === true || config.slotRuleEnabled === true;
  const direction = slotRules && slotDirections.indexOf(config.slotRuleDirection) !== -1 ? config.slotRuleDirection : "none";
  const amount = config.slotRuleAmount !== undefined && !isNaN(Number(config.slotRuleAmount)) ? Number(config.slotRuleAmount) : 0;
  return {
    vehicleTypeId: config.vehicleTypeId || "",
    vehicleTypeName: config.vehicleTypeName || "",
    vehicleSize: config.vehicleSize || getVehicleSize({ vehicleTypeName: config.vehicleTypeName }) || "",
    shipmentType: normalizeShipmentType(config.shipmentType) || config.shipmentType || "",
    active: config.active !== false,
    forms: {
      primary: config.formsPrimary !== false,
      domestic: config.formsDomestic === true,
      import: config.formsImport === true,
      export: config.formsExport === true
    },
    workflows: {
      emptyReturn: config.workflowsEmptyReturn === true,
      emptyPickup: config.workflowsEmptyPickup === true,
      slotRules,
      bookingReceipt: config.workflowsBookingReceipt !== false
    },
    slotRule: {
      enabled: slotRules,
      direction: slotRules ? direction : "none",
      amount: slotRules ? amount : 0
    },
    emailTriggers: {
      bookingCompletedReceipt: config.emailBookingCompletedReceipt !== false,
      emptyReturnNotification: config.emailEmptyReturnNotification === true,
      emptyPickupNotification: config.emailEmptyPickupNotification === true
    },
    updatedAt: new Date().toISOString()
  };
};

const validateWorkflowConfig = (config = {}) => {
  if (!config.vehicleTypeId && !config.vehicleTypeName) {
    throw new Error("Vehicle Type is required.");
  }
  const isOtherVehicleType = !getVehicleSize({ vehicleSize: config.vehicleSize, vehicleTypeName: config.vehicleTypeName });
  if (!isOtherVehicleType && !normalizeShipmentType(config.shipmentType)) {
    throw new Error("Shipment Type is required.");
  }
  if (slotDirections.indexOf(config.slotRuleDirection || "none") === -1) {
    throw new Error("Slot direction must be add, deduct, or none.");
  }
  if (config.slotRuleAmount !== undefined && config.slotRuleAmount !== "" && !Number.isFinite(Number(config.slotRuleAmount))) {
    throw new Error("Slot amount must be numeric.");
  }
};

export const fetchWorkflowConfigurations = () => (dispatch) => {
  const { workflowConfigurationsRef } = firebase;

  dispatch({
    type: FETCH_WORKFLOW_CONFIGS,
    payload: null
  });

  onValue(workflowConfigurationsRef, (snapshot) => {
    const data = snapshot.val() || {};
    const configs = Object.keys(data)
      .map((id) => toFlatWorkflowConfig(id, data[id]))
      .sort((a, b) => `${a.vehicleTypeName || ""} ${a.shipmentType || ""}`.localeCompare(`${b.vehicleTypeName || ""} ${b.shipmentType || ""}`));
    dispatch({
      type: FETCH_WORKFLOW_CONFIGS_SUCCESS,
      payload: configs
    });
  }, (error) => {
    dispatch({
      type: FETCH_WORKFLOW_CONFIGS_FAILED,
      payload: error && error.message ? error.message : "Unable to fetch workflow configurations."
    });
  });
};

export const addWorkflowConfiguration = (config) => (dispatch) => {
  const { workflowConfigurationsRef } = firebase;
  validateWorkflowConfig(config);
  dispatch({ type: EDIT_WORKFLOW_CONFIG, payload: "Add" });
  const payload = {
    ...toFirebaseWorkflowConfig(config),
    createdAt: new Date().toISOString()
  };
  return push(workflowConfigurationsRef, payload);
};

export const updateWorkflowConfiguration = (config) => (dispatch) => {
  const { workflowConfigurationEditRef } = firebase;
  validateWorkflowConfig(config);
  dispatch({ type: EDIT_WORKFLOW_CONFIG, payload: "Update" });
  const payload = toFirebaseWorkflowConfig(config);
  return set(workflowConfigurationEditRef(config.id), payload).then(() => {
    const current = store.getState().workflowconfigs.configs || [];
    dispatch({
      type: FETCH_WORKFLOW_CONFIGS_SUCCESS,
      payload: current.map((item) => item.id === config.id ? toFlatWorkflowConfig(config.id, payload) : item)
    });
  });
};

export const deleteWorkflowConfiguration = (config) => (dispatch) => {
  const { workflowConfigurationEditRef } = firebase;
  dispatch({ type: EDIT_WORKFLOW_CONFIG, payload: "Delete" });
  return remove(workflowConfigurationEditRef(config.id)).then(() => {
    const current = store.getState().workflowconfigs.configs || [];
    dispatch({
      type: FETCH_WORKFLOW_CONFIGS_SUCCESS,
      payload: current.filter((item) => item.id !== config.id)
    });
  });
};
